import { allCompanyGrades, companyStrings } from '@data/company';
import { Country } from '@models/next/Country';
import { PaginationType } from '@models/next/PaginationType';
import { ServiceName } from '@models/next/RoutePlan';
import { f } from '@utils/nextForms';
import { z } from 'zod';

export const CompanyStatus = z.enum([
  'ドラフト',
  '未対応',
  '顧客評価作成待ち',
  '会員番号入力待ち',
  '手続き完了待ち',
  'D2D会員開設申請',
  '手続き完了',
  'FC未承認',
]);

export type CompanyStatus = z.infer<typeof CompanyStatus>;

export const CompanyRole = z.enum(['', 'shipper', 'shipper_agent', 'consignee', 'consignee_agent']);

export type CompanyRole = z.infer<typeof CompanyRole>;

export const CompanyCustomerServiceGrade = z.object({
  grade: z.number().label(companyStrings.grade),
  next_grade: z.number().label(companyStrings.next_grade),
  next_grade_update_date: f.dateString().nullish().label(companyStrings.next_grade_update_date),
});

export type CompanyCustomerServiceGrade = z.infer<typeof CompanyCustomerServiceGrade>;

export const CompanyCustomerServiceGrades = z.object({
  D2D: CompanyCustomerServiceGrade,
  'D2DSHENZHEN+': CompanyCustomerServiceGrade,
  海上速達便: CompanyCustomerServiceGrade,
  D2DSHENZHEN: CompanyCustomerServiceGrade,
  D2DQINGDAO: CompanyCustomerServiceGrade,
} satisfies Record<ServiceName, z.ZodTypeAny>);

export type CompanyCustomerServiceGrades = z.infer<typeof CompanyCustomerServiceGrades>;

export const CompanyCustomerServiceGradeForm = CompanyCustomerServiceGrade;

export type CompanyCustomerServiceGradeForm = z.infer<typeof CompanyCustomerServiceGradeForm>;

export const CompanyFields = z.object({
  id: z.number(),
  company_name: z.string().label(companyStrings.company_name),
  english_name: z.string().nullish(),
  corporate_number: z.string().label(companyStrings.corporate_number),
  zip_code: z.string().label(companyStrings.zip_code),
  address1: z.string().label(companyStrings.address1),
  address2: z.string().label(companyStrings.address2),
  country: Country,
  phone: z.string().label(companyStrings.phone),
  fax: z.string().label(companyStrings.fax),
  start_date: f.dateString().nullish().label(companyStrings.start_date),
  representer_last_name: z.string().label(companyStrings.representer_last_name),
  representer_first_name: z.string().label(companyStrings.representer_first_name),
  legal_capital: z.number().nullish().label(companyStrings.legal_capital),
  industry: z.string().label(companyStrings.industry),
  employee_count: z.number().nullish().label(companyStrings.employee_count),
  transaction_condition: z.boolean().label(companyStrings.transaction_condition),
  transaction_condition_detail: z.string().label(companyStrings.transaction_condition_detail),
  tos_agreed: z.boolean(),
  customer_grade: z.number(),
  customer_next_grade: z.number(),
  customer_next_grade_update_date: f.dateString().nullable(),
  status: CompanyStatus,
  approval_no: z.string().label(companyStrings.approval_no),
  contact_id: z.number().nullable(),
  role: CompanyRole,
  member_no: z.string().nullish().label(companyStrings.member_no),
  importer_code: z.string().label(companyStrings.importer_code),
  real_time_account: z.string().label(companyStrings.real_time_account),
  deferred_payment_account: z.string().label(companyStrings.deferred_payment_account),
  customer_service_grades: CompanyCustomerServiceGrades,
  created_at: z.string(),
  updated_at: z.string(),
});

/*
 * @see: src/models/Company.ts CompanyDetails
 */

export const CompanyDetails = CompanyFields.pick({
  id: true,
  company_name: true,
  english_name: true,
  corporate_number: true,
  zip_code: true,
  address1: true,
  address2: true,
  country: true,
  phone: true,
  fax: true,
  start_date: true,
  representer_last_name: true,
  representer_first_name: true,
  legal_capital: true,
  industry: true,
  employee_count: true,
  transaction_condition: true,
  transaction_condition_detail: true,
  tos_agreed: true,
  customer_grade: true,
  customer_next_grade: true,
  customer_next_grade_update_date: true,
  status: true,
  approval_no: true,
  contact_id: true,
  role: true,
  member_no: true,
  importer_code: true,
  real_time_account: true,
  deferred_payment_account: true,
  customer_service_grades: true,
  created_at: true,
  updated_at: true,
});

export type CompanyDetails = z.infer<typeof CompanyDetails>;

/*
 * @see: src/models/Company.ts companyItem
 */
export const Company = CompanyFields.pick({
  id: true,
  company_name: true,
  english_name: true,
  status: true,
  approval_no: true,
  salesperson_name: true,
  member_no: true,
  corporate_number: true,
  importer_code: true,
  real_time_account: true,
  deferred_payment_account: true,
});

export type Company = z.infer<typeof Company>;

/*
 * @see: src/models/Company.ts patchCompanyBody
 */
export const DashboardPatchCompany = CompanyFields.pick({
  customer_service_grades: true,
});

export type DashboardPatchCompany = z.infer<typeof DashboardPatchCompany>;

export const DashboardCompanySearchQuery = z
  .object({
    page: PaginationType.shape.page,
    page_size: PaginationType.shape.page_size,
    company_name: Company.shape.company_name,
    status: CompanyStatus,
    created_at_after: f.dateString().label(companyStrings.created_at),
    created_at_before: f.dateString().label(companyStrings.created_at),
    approval_no: Company.shape.approval_no,
    email: z.string().label(companyStrings.email),
    fullname: z.string().label(companyStrings.principal_name),
    ordering: z.string().array(),
    salesperson_id: z.number(),
  })
  .partial();

export type DashboardCompanySearchQuery = z.infer<typeof DashboardCompanySearchQuery>;

export const DashboardMemberSearchQuery = z
  .object({
    page: PaginationType.shape.page,
    page_size: PaginationType.shape.page_size,
    member_no: Company.shape.member_no.unwrap().unwrap(),
    created_at_after: f.dateString().label(companyStrings.created_at),
    created_at_before: f.dateString().label(companyStrings.created_at),
    company_name: Company.shape.company_name,
    email: z.string().label(companyStrings.email),
    fullname: z.string().label(companyStrings.principal_name),
    salesperson_name: z.string().label(companyStrings.salesperson_name),
    ordering: z.string().array(),
  })
  .partial();

export type DashboardMemberSearchQuery = z.infer<typeof DashboardMemberSearchQuery>;

export const CompanyPages = z.object({
  count: z.number(),
  results: Company.array(),
});

export type CompanyPages = z.infer<typeof CompanyPages>;

export class CompanyHelper {
  static isMember(v: Pick<CompanyDetails, 'status'>) {
    return v.status === '手続き完了';
  }

  static mapDetailsToCustomerServiceGradesForm(
    v: CompanyDetails,
    name: ServiceName,
  ): CompanyCustomerServiceGradeForm {
    return v.customer_service_grades[name];
  }

  static customerServiceGradesToText(v: CompanyCustomerServiceGrades) {
    return ServiceName.options.map((option) => allCompanyGrades[v[option].grade]).join(' ');
  }
}
